import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, Search, Plus, Eye, Edit, RefreshCw, X, Phone } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const OrderPlusPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '', patientName: '', orderType: 'Verbal Order',
    orderDate: new Date().toISOString().split('T')[0], physicianName: '',
    orderDetails: '', readBackVerified: false, notifiedTime: '', status: 'Pending'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, patientsRes] = await Promise.all([
        api.get('/orders'),
        api.get('/patients')
      ]);
      if (ordersRes.data.success) setOrders(ordersRes.data.orders || []);
      if (patientsRes.data.success) setPatients(patientsRes.data.patients || []);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientName || !formData.orderDetails) {
      toast.error('Please fill in patient and order details');
      return;
    }
    try {
      const res = await api.post('/orders', {
        ...formData,
        orderType: 'Verbal Order Plus'
      });
      if (res.data.success) {
        toast.success('Verbal order created');
        setOrders(prev => [...prev, res.data.order]);
        setShowModal(false);
        setFormData({
          patientId: '', patientName: '', orderType: 'Verbal Order',
          orderDate: new Date().toISOString().split('T')[0], physicianName: '',
          orderDetails: '', readBackVerified: false, notifiedTime: '', status: 'Pending'
        });
      }
    } catch (error) {
      toast.error('Failed to create order');
    }
  };

  const verbalOrders = orders.filter(o => o.orderType?.includes('Verbal'));

  const filteredOrders = verbalOrders.filter(o =>
    o.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.physicianName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: verbalOrders.length,
    pending: verbalOrders.filter(o => o.status === 'Pending').length,
    signed: verbalOrders.filter(o => o.status === 'Signed').length
  };

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="order-plus-page">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Phone className="w-6 h-6 text-orange-600" />
                Order Plus (Verbal Order Plus)
              </h1>
              <p className="text-gray-500 text-sm mt-1">Document and track verbal orders with read-back verification</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
              <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />New Verbal Order
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-700">{stats.total}</div>
            <div className="text-sm text-orange-600">Total Verbal Orders</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <div className="text-sm text-yellow-600">Pending Signature</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.signed}</div>
            <div className="text-sm text-green-600">Signed</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by patient or physician..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No verbal orders found. Click "New Verbal Order" to create one.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Order Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Physician</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Read-Back</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{order.patientName}</td>
                    <td className="px-6 py-4">{order.orderDate}</td>
                    <td className="px-6 py-4">{order.physicianName || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${order.readBackVerified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {order.readBackVerified ? 'Verified' : 'Not Verified'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${order.status === 'Signed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline"><Eye className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline"><Edit className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">New Verbal Order</h2>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Patient *</label>
                  <select value={formData.patientId} onChange={(e) => {
                    const p = patients.find(p => p.id === e.target.value);
                    setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: p ? `${p.firstName} ${p.lastName}` : '' }));
                  }} className="w-full border rounded-md px-3 py-2">
                    <option value="">Select Patient</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Order Date</label>
                    <Input type="date" value={formData.orderDate} onChange={(e) => setFormData(prev => ({ ...prev, orderDate: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Time Notified</label>
                    <Input type="time" value={formData.notifiedTime} onChange={(e) => setFormData(prev => ({ ...prev, notifiedTime: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Physician Name</label>
                  <Input value={formData.physicianName} onChange={(e) => setFormData(prev => ({ ...prev, physicianName: e.target.value }))} placeholder="Dr. Smith" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Order Details *</label>
                  <textarea value={formData.orderDetails} onChange={(e) => setFormData(prev => ({ ...prev, orderDetails: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={3} placeholder="Enter verbal order details..." />
                </div>
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <input type="checkbox" checked={formData.readBackVerified} onChange={(e) => setFormData(prev => ({ ...prev, readBackVerified: e.target.checked }))} />
                  <label className="text-sm text-blue-800">I have read back the order to the physician and verified accuracy</label>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={handleSubmit}>Save Order</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OrderPlusPage;
